import { AnchorHTMLAttributes, MouseEvent as ReactMouseEvent, ReactNode, useCallback, useEffect, useState } from 'react'
import type { LocationState } from './appTypes'

const navigationEvent = 'authem:navigate'
export function useLocation(): LocationState {
  const read = () => ({ pathname: window.location.pathname, search: window.location.search })
  const [location, setLocation] = useState<LocationState>(read)
  useEffect(() => {
    const update = () => setLocation(read())
    window.addEventListener('popstate', update)
    window.addEventListener(navigationEvent, update)
    update() // resync: a child Navigate effect may have fired before these listeners attached
    return () => { window.removeEventListener('popstate', update); window.removeEventListener(navigationEvent, update) }
  }, [])
  return location
}

export function useNavigate() {
  return useCallback((to: string, options?: { replace?: boolean }) => {
    if (options?.replace) window.history.replaceState({}, '', to)
    else window.history.pushState({}, '', to)
    window.dispatchEvent(new Event(navigationEvent))
  }, [])
}

export function Link({ to, children, onClick, ...props }: { to: string; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const navigate = useNavigate()
  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigate(to)
  }
  return <a {...props} href={to} onClick={handleClick}>{children}</a>
}

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const navigate = useNavigate()
  useEffect(() => { navigate(to, { replace }) }, [navigate, replace, to])
  return null
}

export function useSearchParams(): [URLSearchParams] {
  const location = useLocation()
  return [new URLSearchParams(location.search)]
}
